/**
 * The table under every chart, and its CSV (decision 5.4).
 *
 * The rows are the ones the chart drew, so the three views - picture, table, file - cannot disagree.
 */

import { Download } from "lucide-react";
import { useState } from "react";

import { saveBlob } from "@/api/client";
import { Button } from "@/components/ui/button";

export type Row = Record<string, string | number | null | undefined>;

export function TableView({ title, rows, caption }: { title: string; rows: Row[]; caption?: string }) {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  const columns = Object.keys(rows[0]);

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? "Hide table" : "Table view"}
        </button>
        <Button size="xs" variant="ghost" onClick={() => downloadCsv(title, rows)}>
          <Download aria-hidden="true" /> CSV
        </Button>
      </div>
      {open && (
        <div className="mt-2 max-h-96 overflow-auto rounded-lg border border-white/10">
          <table className="w-full text-left text-xs">
            <caption className="sr-only">{caption ?? title}</caption>
            <thead className="sticky top-0 bg-[#16181d] text-muted-foreground">
              <tr>
                {columns.map((column) => (
                  <th key={column} scope="col" className="border-b border-white/10 px-2 py-1.5 font-medium">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className="odd:bg-white/[0.02]">
                  {columns.map((column) => (
                    <td key={column} className="px-2 py-1.5 align-top">
                      {format(row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function format(value: Row[string]): string {
  if (value === null || value === undefined) return "";
  return typeof value === "number" ? String(Math.round(value * 1000) / 1000) : value;
}

export function toCsv(rows: Row[]): string {
  const columns = Object.keys(rows[0]);
  const body = rows.map((row) => columns.map((column) => cell(row[column])).join(","));
  return [columns.map(cell).join(","), ...body].join("\r\n");
}

export function downloadCsv(title: string, rows: Row[]): void {
  // A BOM, so Excel opens accented text and symbols correctly rather than as mojibake.
  const blob = new Blob([`﻿${toCsv(rows)}`], { type: "text/csv;charset=utf-8" });
  saveBlob(blob, `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}.csv`);
}

function cell(value: Row[string]): string {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
